<main aria-label="Pricing Table">
  <section>
    <div
      class="selection:cpt-text-calenso-white-color selection:cpt-bg-calenso-primary-fill"
      [ngClass]="{'cpt-m-5': _renderModeSignal() === sales}"
    >
      <!-- Hero — sales mode only. Dashboard mode is embedded inside the
           dashboard chrome which already provides its own page header, so
           the hero would just duplicate the framing. -->
      @if (_renderModeSignal() === sales) {
        <header class="pricing-hero">
          <span class="pricing-hero-eyebrow">{{ 'pricing_table.hero_eyebrow' | translate }}</span>
          <h1 class="pricing-hero-title">
            {{ 'pricing_table.hero_title_lead' | translate }}
            <span class="pricing-hero-accent">{{ 'pricing_table.hero_title_accent' | translate }}</span>
          </h1>
          <p class="pricing-hero-subtitle">
            {{ 'pricing_table.hero_subtitle' | translate: brandParams }}
          </p>
        </header>
      }

      @if (_renderModeSignal() === sales) {
        <!-- Sales-mode toggle bar — centered CURRENCY + BILLING controls.
             The page title is already supplied by the hero above; the old
             "Calenso Price Calculator" H1 here would have duplicated it. -->
        <header class="cpt-flex cpt-justify-center cpt-items-center cpt-mb-9 cpt-flex-wrap">
          <div class="cpt-flex cpt-gap-6 cpt-flex-wrap cpt-justify-center cpt-items-center">
            <div class="toggle-cluster">
              <span class="toggle-label">{{ 'pricing_table.language_label' | translate }}</span>
              <div class="toggle-group" role="group" aria-label="Language selection">
                <button type="button" class="toggle-btn" [class.active]="_languageSignal() === de_CH"
                  (click)="setLanguage(de_CH)" [attr.aria-pressed]="_languageSignal() === de_CH">DE</button>
                <button type="button" class="toggle-btn" [class.active]="_languageSignal() === en_US"
                  (click)="setLanguage(en_US)" [attr.aria-pressed]="_languageSignal() === en_US">EN</button>
              </div>
            </div>
            <div class="toggle-cluster">
              <span class="toggle-label">{{ 'pricing_table.currency_label' | translate }}</span>
              <div class="toggle-group" role="group" aria-label="Currency selection">
                <button type="button" class="toggle-btn" [class.active]="_currencySignal() === chf"
                  (click)="setCurrency(chf)" [attr.aria-pressed]="_currencySignal() === chf"
                  [attr.aria-label]="'pricing_table.select_chf_currency' | translate">CHF</button>
                <button type="button" class="toggle-btn" [class.active]="_currencySignal() === eur"
                  (click)="setCurrency(eur)" [attr.aria-pressed]="_currencySignal() === eur"
                  [attr.aria-label]="'pricing_table.select_eur_currency' | translate">EUR</button>
                <button type="button" class="toggle-btn" [class.active]="_currencySignal() === usd"
                  (click)="setCurrency(usd)" [attr.aria-pressed]="_currencySignal() === usd"
                  [attr.aria-label]="'pricing_table.select_usd_currency' | translate">USD</button>
                <button type="button" class="toggle-btn" [class.active]="_currencySignal() === gbp"
                  (click)="setCurrency(gbp)" [attr.aria-pressed]="_currencySignal() === gbp"
                  [attr.aria-label]="'pricing_table.select_gbp_currency' | translate">GBP</button>
              </div>
            </div>
            <div class="toggle-cluster">
              <span class="toggle-label">{{ 'pricing_table.billing_label' | translate }}</span>
              <div class="toggle-group" role="group" aria-label="Billing interval">
                @if (!_hideMonthlyIntervalSignal()) {
                  <button type="button" class="toggle-btn" [class.active]="_intervalSignal() === monthly"
                    (click)="toggleInterval(monthly)" [attr.aria-pressed]="_intervalSignal() === monthly">
                    {{ "pricing_table.monthly" | translate }}
                  </button>
                }
                <button type="button" class="toggle-btn" [class.active]="_intervalSignal() === yearly"
                  (click)="toggleInterval(yearly)" [attr.aria-pressed]="_intervalSignal() === yearly">
                  {{ "pricing_table.annual" | translate }}
                  <span class="toggle-btn-tag">{{ 'pricing_table.paid_annually' | translate }}</span>
                </button>
              </div>
            </div>
          </div>
        </header>
      }

      <div class="pricing-shell" [class.pricing-shell--sales]="_renderModeSignal() === sales">
        <!-- Calculator wrapper — bordered only when it actually has content.
             When `show-custom-plan-section` isn't passed AND we're not in
             sales mode, the wrapper would otherwise be an empty bordered
             card with just a BILLING toggle floating inside. -->
        <div class="pricing-shell-calculator cpt-text-base cpt-font-semibold"
             [class.cpt-border]="_showCustomPlanSectionSignal()"
             [class.cpt-border-solid]="_showCustomPlanSectionSignal()"
             [class.cpt-rounded]="_showCustomPlanSectionSignal()"
             [class.cpt-p-3]="_showCustomPlanSectionSignal()"
             [class.pricing-shell-calculator--compact]="_renderModeSignal() === dashboard">
          <div class="cpt-flex cpt-flex-col-reverse cpt-items-center cpt-gap-3 md:cpt-flex-row md:cpt-justify-between">
            <!-- "Manage user profile" toggle — dashboard only. Sales mode
                 keeps the calculator auto-open (see checkForSalesMode()), so
                 the toggle would be a confusing no-op there. -->
            @if (_showCustomPlanSectionSignal() && _renderModeSignal() !== sales) {
              <div class="cpt-text-sm md:cpt-text-md">
                <button
                  type="button"
                  class="cpt-bg-transparent cpt-border-none cpt-text-calenso-neon-blue-color cpt-cursor-pointer cpt-border-b-2 cpt-border-calenso-neon-blue-color cpt-p-1 cpt-rounded focus:cpt-outline-none focus:cpt-ring-2 focus:cpt-ring-calenso-neon-blue-color focus:cpt-ring-offset-2"
                  (click)="toggleUserProfile()"
                  [attr.aria-label]="isCustomPlanSelectionEnabledSignal() ? ('pricing_table.close_custom_plan' | translate) : ('pricing_table.open_custom_plan' | translate)"
                  [attr.aria-expanded]="isCustomPlanSelectionEnabledSignal()"
                >
                  @if(isCustomPlanSelectionEnabledSignal()) {
                    {{ "pricing_table.close" | translate }}
                  } @else {
                    {{ "pricing_table.manage_user_profile" | translate }}
                  }
                </button>
              </div>
            } @else {
              <div></div>
            }

            <!-- Calculator BILLING toggle — dashboard only. Sales mode has
                 the same toggle in the page-level header (above the tier
                 grid), and rendering both creates a duplicate that flips
                 the same _intervalSignal. -->
            @if (_renderModeSignal() !== sales) {
              <div class="toggle-cluster">
                <span class="toggle-label">{{ 'pricing_table.billing_label' | translate }}</span>
                <div class="toggle-group" role="group" aria-label="Billing interval">
                  @if (!_hideMonthlyIntervalSignal()) {
                    <button type="button" class="toggle-btn" [class.active]="_intervalSignal() === monthly"
                      (click)="toggleInterval(monthly)" [attr.aria-pressed]="_intervalSignal() === monthly">
                      {{ "pricing_table.monthly" | translate }}
                    </button>
                  }
                  <button type="button" class="toggle-btn" [class.active]="_intervalSignal() === yearly"
                    (click)="toggleInterval(yearly)" [attr.aria-pressed]="_intervalSignal() === yearly">
                    {{ "pricing_table.annual" | translate }}
                    <span class="toggle-btn-tag">{{ 'pricing_table.paid_annually' | translate }}</span>
                  </button>
                </div>
              </div>
            }
          </div>
        </div>
        @if (isCustomPlanSelectionEnabledSignal() && _showCustomPlanSectionSignal()) {
          <section aria-label="Custom Plan Selection" class="pricing-shell-customplan cpt-mt-4">
            <!-- Sales mode gets a section header so the calculator reads as
                 its own surface beneath the tier grid; dashboard keeps the
                 calculator inline above the cards without the header. -->
            @if (_renderModeSignal() === sales) {
              <header class="pricing-shell-customplan-header">
                <h2 class="pricing-shell-customplan-title">{{ 'pricing_table.sales_calculator_title' | translate }}</h2>
                <p class="pricing-shell-customplan-subtitle">{{ 'pricing_table.sales_calculator_subtitle' | translate }}</p>
              </header>
            }
            <!-- Stepper cards — demo-matched style: small chip label, round
                 +/- buttons around a boxed number input, per-unit cost hint.
                 Costs come from EXTRA_*_COST constants; the {currency} interpolation
                 keeps the hint correct when the user flips CHF / EUR / USD / GBP. -->
            <div class="cpt-grid cpt-gap-3 md:cpt-grid-cols-2 lg:cpt-grid-cols-4">
              <div class="calc-input-card">
                <div class="calc-label">{{ "pricing_table.change_number_of_user" | translate }}</div>
                <div class="calc-stepper" role="group" [attr.aria-label]="'pricing_table.change_number_of_user' | translate">
                  <button type="button" class="calc-step-btn"
                    [disabled]="customPlanUserSignal() <= minLimit"
                    (click)="decrement(customPlanUserType)"
                    [attr.aria-label]="'pricing_table.decrement_user' | translate">−</button>
                  <input type="number" name="userCount" class="calc-step-input"
                    [(ngModel)]="customPlanUserSignal"
                    appMaxInputValue="{{ maxLimit }}"
                    (keyup)="changeUserProfile(customPlanUserSignal(), customPlanUserType)"
                    [attr.aria-label]="'pricing_table.user_number_input' | translate"
                    min="{{ minLimit }}" max="{{ maxLimit }}">
                  <button type="button" class="calc-step-btn"
                    [disabled]="customPlanUserSignal() >= maxLimit"
                    (click)="increment(customPlanUserType)"
                    [attr.aria-label]="'pricing_table.increment_user' | translate">+</button>
                </div>
                <div class="calc-cost">+ {{ currencySymbolSignal() }} {{ extraUserCost }} {{ 'pricing_table.per_month_above_tier_min' | translate }}</div>
              </div>
              <div class="calc-input-card">
                <div class="calc-label">{{ "pricing_table.change_number_of_store" | translate }}</div>
                <div class="calc-stepper" role="group" [attr.aria-label]="'pricing_table.change_number_of_store' | translate">
                  <button type="button" class="calc-step-btn"
                    [disabled]="customPlanStoreSignal() <= minLimit"
                    (click)="decrement(customPlanStoreType)"
                    [attr.aria-label]="'pricing_table.decrement_store' | translate">−</button>
                  <input type="number" name="storeCount" class="calc-step-input"
                    [(ngModel)]="customPlanStoreSignal"
                    appMaxInputValue="{{ maxLimit }}"
                    (keyup)="changeUserProfile(customPlanStoreSignal(), customPlanStoreType)"
                    [attr.aria-label]="'pricing_table.store_number_input' | translate"
                    min="{{ minLimit }}" max="{{ maxLimit }}">
                  <button type="button" class="calc-step-btn"
                    [disabled]="customPlanStoreSignal() >= maxLimit"
                    (click)="increment(customPlanStoreType)"
                    [attr.aria-label]="'pricing_table.increment_store' | translate">+</button>
                </div>
                <div class="calc-cost">+ {{ currencySymbolSignal() }} {{ extraStoreCost }} {{ 'pricing_table.per_month_above_tier_min' | translate }}</div>
              </div>
              <div class="calc-input-card">
                <div class="calc-label">{{ "pricing_table.change_number_of_group_appointment" | translate }}</div>
                <div class="calc-stepper" role="group" [attr.aria-label]="'pricing_table.change_number_of_group_appointment' | translate">
                  <button type="button" class="calc-step-btn"
                    [disabled]="customPlanGroupAppointmentSignal() <= minLimit"
                    (click)="decrement(customPlanGroupAppointmentType)"
                    [attr.aria-label]="'pricing_table.decrement_group_appointment' | translate">−</button>
                  <input type="number" name="groupAppointmentCount" class="calc-step-input"
                    [(ngModel)]="customPlanGroupAppointmentSignal"
                    appMaxInputValue="{{ maxLimit }}"
                    (keyup)="changeUserProfile(customPlanGroupAppointmentSignal(), customPlanGroupAppointmentType)"
                    [attr.aria-label]="'pricing_table.group_appointment_number_input' | translate"
                    min="{{ minLimit }}" max="{{ maxLimit }}">
                  <button type="button" class="calc-step-btn"
                    [disabled]="customPlanGroupAppointmentSignal() >= maxLimit"
                    (click)="increment(customPlanGroupAppointmentType)"
                    [attr.aria-label]="'pricing_table.increment_group_appointment' | translate">+</button>
                </div>
                <div class="calc-cost">+ {{ currencySymbolSignal() }} {{ extraGroupAppointmentCost }} {{ 'pricing_table.per_month_above_tier_min' | translate }}</div>
              </div>
              <div class="calc-input-card">
                <div class="calc-label">{{ "pricing_table.change_number_of_conference" | translate }}</div>
                <div class="calc-stepper" role="group" [attr.aria-label]="'pricing_table.change_number_of_conference' | translate">
                  <button type="button" class="calc-step-btn"
                    [disabled]="customPlanConferenceSignal() <= minConferenceLimit"
                    (click)="decrement(customPlanConferenceType)"
                    [attr.aria-label]="'pricing_table.decrement_conference' | translate">−</button>
                  <input type="number" name="conferenceCount" class="calc-step-input"
                    [(ngModel)]="customPlanConferenceSignal"
                    appMaxInputValue="{{ maxLimit }}"
                    (keyup)="changeUserProfile(customPlanConferenceSignal(), customPlanConferenceType)"
                    [attr.aria-label]="'pricing_table.conference_number_input' | translate"
                    min="{{ minConferenceLimit }}" max="{{ maxLimit }}">
                  <button type="button" class="calc-step-btn"
                    [disabled]="customPlanConferenceSignal() >= maxLimit"
                    (click)="increment(customPlanConferenceType)"
                    [attr.aria-label]="'pricing_table.increment_conference' | translate">+</button>
                </div>
                <div class="calc-cost">+ {{ currencySymbolSignal() }} {{ extraConferenceCost }} {{ 'pricing_table.per_month_above_tier_min' | translate }}</div>
              </div>
            </div>
            <!-- Custom plan pricing calculation start -->
            <div class="cpt-mt-4 cpt-flex cpt-justify-center md:cpt-justify-end md:cpt-self-end">
              <div class="cpt-max-w-fit cpt-text-center md:cpt-text-right">
                <div class="cpt-text-center md:cpt-text-left cpt-tabular-nums">
                  @if (customSelectedPlanSignal()?.id !== enterpriseId) {
                    <!-- Receipt-style breakdown: line items first, grand total last. -->
                    <div class="cpt-text-sm cpt-text-calenso-steel-blue-color cpt-min-w-[240px] md:cpt-min-w-[260px]" role="list" aria-label="Price breakdown">
                      <div class="cpt-flex cpt-justify-between cpt-items-baseline cpt-gap-3" role="listitem">
                        <span class="cpt-font-semibold cpt-text-calenso-black-color">
                          {{ customSelectedPlanSignal()?.subscription_title }}
                          <span class="cpt-font-normal cpt-opacity-70">({{ "pricing_table.base" | translate }})</span>
                        </span>
                        <span class="cpt-font-medium cpt-text-calenso-black-color">
                          {{ currencySymbolSignal() }} {{ customSelectedPlanSignal()?.[(_currencySignal() | lowercase) || eur.toLowerCase()]?.[_intervalSignal()]?.original_price | number: decimalDigits: decimalLocal }}
                        </span>
                      </div>
                      @if (customSelectedPlanSignal()?.min_workers < customPlanUserSignal()) {
                        <div class="cpt-flex cpt-justify-between cpt-items-baseline cpt-gap-3 cpt-mt-1" role="listitem">
                          <span>
                            + {{ customPlanUserSignal() - customSelectedPlanSignal()?.min_workers }} ×
                            {{ "pricing_table.per_user_calender" | translate }}
                            <span class="cpt-opacity-70">({{ currencySymbolSignal() }}&nbsp;{{ extraUserCost }})</span>
                          </span>
                          <span>{{ currencySymbolSignal() }} {{ (customPlanUserSignal() - customSelectedPlanSignal()?.min_workers) * extraUserCost | number: decimalDigits: decimalLocal }}</span>
                        </div>
                      }
                      @if (customSelectedPlanSignal()?.min_store < customPlanStoreSignal()) {
                        <div class="cpt-flex cpt-justify-between cpt-items-baseline cpt-gap-3 cpt-mt-1" role="listitem">
                          <span>
                            + {{ customPlanStoreSignal() - customSelectedPlanSignal()?.min_store }} ×
                            {{ "pricing_table.per_store" | translate }}
                            <span class="cpt-opacity-70">({{ currencySymbolSignal() }}&nbsp;{{ extraStoreCost }})</span>
                          </span>
                          <span>{{ currencySymbolSignal() }} {{ (customPlanStoreSignal() - customSelectedPlanSignal()?.min_store) * extraStoreCost | number: decimalDigits: decimalLocal }}</span>
                        </div>
                      }
                      @if (customSelectedPlanSignal()?.min_group_appointment < customPlanGroupAppointmentSignal()) {
                        <div class="cpt-flex cpt-justify-between cpt-items-baseline cpt-gap-3 cpt-mt-1" role="listitem">
                          <span>
                            + {{ customPlanGroupAppointmentSignal() - customSelectedPlanSignal()?.min_group_appointment }} ×
                            {{ "pricing_table.per_group_appointment" | translate }}
                            <span class="cpt-opacity-70">({{ currencySymbolSignal() }}&nbsp;{{ extraGroupAppointmentCost }})</span>
                          </span>
                          <span>{{ currencySymbolSignal() }} {{ (customPlanGroupAppointmentSignal() - customSelectedPlanSignal()?.min_group_appointment) * extraGroupAppointmentCost | number: decimalDigits: decimalLocal }}</span>
                        </div>
                      }
                      @if (customSelectedPlanSignal()?.min_conference < customPlanConferenceSignal()) {
                        <div class="cpt-flex cpt-justify-between cpt-items-baseline cpt-gap-3 cpt-mt-1" role="listitem">
                          <span>
                            + {{ customPlanConferenceSignal() - customSelectedPlanSignal()?.min_conference }} ×
                            {{ "pricing_table.per_conference" | translate }}
                            <span class="cpt-opacity-70">({{ currencySymbolSignal() }}&nbsp;{{ extraConferenceCost }})</span>
                          </span>
                          <span>{{ currencySymbolSignal() }} {{ (customPlanConferenceSignal() - customSelectedPlanSignal()?.min_conference) * extraConferenceCost | number: decimalDigits: decimalLocal }}</span>
                        </div>
                      }
                    </div>
                    <div class="cpt-border-t-2 cpt-border-calenso-powder-blue-color cpt-mt-2 cpt-mb-2"></div>
                    <div class="cpt-flex cpt-justify-between cpt-items-baseline cpt-gap-3" role="text" aria-label="Custom plan total price">
                      <span class="cpt-text-sm cpt-font-semibold cpt-uppercase cpt-tracking-wide cpt-text-calenso-steel-blue-color">
                        {{ "pricing_table.total" | translate }}
                      </span>
                      <span class="cpt-text-2xl cpt-font-bold">
                        {{ currencySymbolSignal() }} {{ customPlanTotalAmountSignal() | number: decimalDigits: decimalLocal }}
                        <span class="cpt-text-md cpt-font-normal"> / {{ "pricing_table.month" | translate }}</span>
                      </span>
                    </div>
                  } @else {
                    <div class="cpt-text-center md:cpt-text-right">
                      <span class="cpt-text-2xl cpt-font-bold">
                        {{ "pricing_table.price_on_request" | translate }}
                      </span>
                    </div>
                  }
                </div>
              </div>
            </div>
            <!-- Custom plan pricing calculation end -->

            <!-- "You also unlock with X" benefits panel — sales-only.
                 Re-frames the recommended tier's feature_list as bonuses
                 the prospect unlocks at the auto-picked tier. Dashboard
                 already shows the same features inside each tier card,
                 so this panel would just duplicate content there.
                 Refreshes whenever customSelectedPlanSignal() changes
                 (stepper interaction). -->
            @if (customSelectedPlanSignal() && _renderModeSignal() === sales) {
              <div class="benefits-panel cpt-mt-6 cpt-p-4 cpt-rounded">
                <header class="cpt-flex cpt-items-center cpt-gap-2 cpt-mb-3 cpt-flex-wrap">
                  <span class="cpt-text-xs cpt-font-bold cpt-uppercase cpt-tracking-wide cpt-text-calenso-steel-blue-color">
                    ✨ {{ 'pricing_table.you_also_unlock' | translate }}
                  </span>
                  <span class="cpt-bg-gradient-primary cpt-text-calenso-white-color cpt-text-xs cpt-font-bold cpt-px-2.5 cpt-py-0.5 cpt-rounded-full cpt-uppercase cpt-tracking-wide">
                    {{ customSelectedPlanSignal()?.subscription_title }}
                  </span>
                </header>
                <ul class="cpt-grid cpt-grid-cols-1 md:cpt-grid-cols-2 cpt-gap-x-4 cpt-gap-y-2 cpt-list-none cpt-p-0 cpt-m-0" role="list">
                  @for (feature of customSelectedPlanSignal()?.feature_list?.[_renderModeSignal()] || []; track feature[_languageSignal()]) {
                    @if (feature?.include !== false) {
                      <li class="cpt-flex cpt-items-start cpt-gap-2 cpt-text-sm" role="listitem">
                        <span class="cpt-text-calenso-neon-blue-color cpt-flex-shrink-0 cpt-font-bold" aria-hidden="true">✓</span>
                        <span class="cpt-text-calenso-black-color cpt-word-break" [innerHTML]="feature[_languageSignal()] | calioSafe: 'html'"></span>
                      </li>
                    }
                  }
                </ul>
              </div>
            }
          </section>
        }
        <div class="tier-grid cpt-mt-6">
          @for (plan of pricingListSignal(); track plan.id) {
            <article
              [ngClass]="{
                'active-plan': (
                _renderModeSignal() === dashboard &&
                plan.id === _currentSubscriptionSignal()?.subscription_type_id &&
                _intervalSignal() === _currentSubscriptionSignal()?.subscription_interval),
                'popular-plan':(
                  plan.name === _popularPlanSignal() &&
                  _currentSubscriptionSignal()?.subscription_type_id !== subscriptionIds[_popularPlanSignal()]
                ),
                'cpt-opacity-50 disabled-plan': !availablePlansSignal().includes(plan.id)
              }"
              class="pricing-card pricing-card--v2 cpt-relative"
              [attr.aria-label]="plan.subscription_title + ' pricing plan'"
            >
              <!-- Floating active/popular badges — pinned to the top edge, demo-style -->
              <div class="pricing-card-badge-wrap">
                <div class="pricing-card-badge pricing-card-badge--active" role="status" [attr.aria-label]="'pricing_table.active' | translate">
                  {{ "pricing_table.active" | translate }}
                </div>
                <div class="pricing-card-badge pricing-card-badge--popular cpt-bg-gradient-primary" role="status" [attr.aria-label]="'pricing_table.best_value' | translate">
                  ✨ {{ "pricing_table.best_value" | translate }}
                </div>
              </div>
              <!-- Persona tag -->
              <div class="cpt-mb-2">
                <span class="persona-tag">
                  {{ ('pricing_table.persona_' + plan.name) | translate }}
                </span>
              </div>
              <!-- Tier name -->
              <h2 class="pricing-card-name">
                {{ plan.subscription_title }}
              </h2>
              <!-- Description — sales mode prefers the marketing-led
                   subscription_subtitle_sales when set; dashboard mode
                   uses the persona-led subscription_subtitle. -->
              <p class="pricing-card-subtitle">
                @if (_renderModeSignal() === sales && plan.subscription_subtitle_sales) {
                  {{ plan.subscription_subtitle_sales[_languageSignal()] }}
                } @else {
                  {{ plan.subscription_subtitle[_languageSignal()] }}
                }
              </p>
              <!-- Price -->
              <div class="pricing-card-price-block">
                @if (hasVisiblePrice(plan)) {
                  <!-- Align by typographic baseline (cpt-items-baseline). Since "EUR",
                       digits, and "/ Month" have no descenders, baseline alignment
                       gives true visible-bottom alignment for free. flex-end aligned
                       BOX bottoms, which left small-font glyphs hanging ~1.3px below
                       the digit baseline because Open Sans's descender slack scales
                       with font-size. flex-wrap lets the period drop to a new line on
                       narrow cards / longer prices (e.g. "EUR 269.00 / Monat" in
                       German previously truncated to "Mona" on the Starter column).
                       pricing-card-period has white-space: nowrap so "/ Monat" stays
                       intact when it wraps. -->
                  <div class="cpt-flex cpt-flex-wrap cpt-items-baseline cpt-gap-x-1 cpt-gap-y-0">
                    <span class="pricing-card-currency">{{ currencySymbolSignal() }}</span>
                    <span class="pricing-card-amount">
                      @if (plan?.name === appliedPlanSignal()?.name) {
                        {{ customPlanTotalAmountSignal() | number: decimalDigits: decimalLocal }}
                      } @else {
                        {{ plan?.[_currencySignal() | lowercase]?.[_intervalSignal()]?.original_price | number: decimalDigits: decimalLocal }}
                      }
                    </span>
                    <span class="pricing-card-period">/ {{ "pricing_table.month" | translate }}</span>
                  </div>
                  <div class="pricing-card-billed">
                    @if (_intervalSignal() === monthly) {
                      {{ "pricing_table.charged_monthly" | translate }}
                    } @else {
                      {{ "pricing_table.charged_annually" | translate }}
                    }
                  </div>
                } @else {
                  <div class="pricing-card-amount pricing-card-amount--custom">
                    {{ "pricing_table.upon_request" | translate }}
                  </div>
                }
              </div>
              <!-- Upgrade button -->
              @if (_renderModeSignal() !== sales) {
                <div class="pricing-card-cta">
                  <ng-container [ngTemplateOutlet]="planUpgradeButton" [ngTemplateOutletContext]="{ plan: plan }" />
                </div>
              }
              <!-- Feature list -->
              <div class="pricing-card-features">
                <h3 class="pricing-card-features-header">
                  {{ plan.feature_list_title[_languageSignal()] }}
                </h3>
                <ul class="pricing-card-feature-list" role="list">
                  @for (feature of plan.feature_list[_renderModeSignal()]; track feature[_languageSignal()]) {
                    <li
                      class="pricing-card-feature"
                      [class.pricing-card-feature--excluded]="feature?.include === false"
                      role="listitem">
                      @if (feature?.include === false) {
                        <span class="pricing-card-feature-icon pricing-card-feature-icon--cross" aria-hidden="true">
                          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M9 3L3 9"/><path d="M3 3L9 9"/>
                          </svg>
                        </span>
                      } @else {
                        <span class="pricing-card-feature-icon" aria-hidden="true">
                          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="2 6 5 9 10 3"/>
                          </svg>
                        </span>
                      }
                      <span class="pricing-card-feature-text" [innerHTML]="feature[_languageSignal()] | calioSafe: 'html'"></span>
                    </li>
                  }
                </ul>
              </div>
            </article>
          }
        </div>
      </div>

      <!-- Comparison table — sales-only. Dashboard already shows the same
           data inside each tier card's feature list; the standalone
           comparison panel would just duplicate it. Data sourced from
           core/constants/v2/comparison.constants.ts; row labels bilingual
           via the multiLanguage shape, no separate i18n keys. -->
      @if (_renderModeSignal() === sales) {
      <section class="pricing-shell-compare cpt-mt-12" aria-label="Feature comparison">
        <div class="cpt-text-center">
          <button type="button"
            class="compare-toggle"
            [class.compare-toggle--open]="showCompareSignal()"
            [attr.aria-expanded]="showCompareSignal()"
            aria-controls="comparisonTable"
            (click)="toggleCompare()">
            <span>
              @if (showCompareSignal()) {
                {{ 'pricing_compare.hide_comparison' | translate }}
              } @else {
                {{ 'pricing_table.compare_all_features' | translate }}
              }
            </span>
            <svg class="compare-chevron" width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="3 5 7 9 11 5"/>
            </svg>
          </button>
        </div>

        @if (showCompareSignal()) {
          <div class="compare-panel cpt-mt-6" id="comparisonTable">
            <table class="compare-table">
              <thead>
                <tr>
                  <th>{{ 'pricing_compare.feature_column' | translate }}</th>
                  <th>Basic</th>
                  <th>Starter</th>
                  <th class="compare-th-popular">Team ⭐</th>
                  <th>Enterprise</th>
                </tr>
              </thead>
              <tbody>
                @for (category of compareCategories; track category.title.en_US) {
                  <tr class="compare-group-row">
                    <td colspan="5">{{ category.title[_languageSignal()] }}</td>
                  </tr>
                  @for (row of category.rows; track row.feature.en_US) {
                    <tr>
                      <td>{{ row.feature[_languageSignal()] }}</td>
                      @for (cell of [row.basic, row.starter, row.team, row.enterprise]; track $index) {
                        <td>
                          @if (isCellBool(cell)) {
                            @if (cell === true) {
                              <span class="compare-yes" aria-label="Included">✓</span>
                            } @else {
                              <span class="compare-no" aria-label="Not included">—</span>
                            }
                          } @else if (isCellAddon(cell)) {
                            <span class="compare-addon" aria-label="Available as add-on">+</span>
                          } @else {
                            <span class="compare-num">{{ cell }}</span>
                          }
                        </td>
                      }
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>
        }
      </section>
      }

      <!-- On-demand add-ons — available across all tiers via contact-support flow.
           Surfaces 12 predefined add-ons (analytics, room booking, OTP, timezone,
           customer prefill, multi-partner worker, resource planning, HubSpot, Entra
           ID user sync, dynamic combi, Graph API logging, condition engine). -->
      <section aria-label="On-demand Add-ons" class="pricing-shell-addons cpt-mt-12">
        <header class="cpt-text-center cpt-mb-6">
          <h2 class="cpt-text-xl md:cpt-text-2xl cpt-font-bold cpt-font-sans">
            {{ 'pricing_addons.section_title' | translate }}
          </h2>
          <p class="cpt-mt-2 cpt-text-sm md:cpt-text-base cpt-text-calenso-steel-blue-color cpt-max-w-2xl cpt-mx-auto">
            {{ 'pricing_addons.section_subtitle' | translate }}
          </p>
        </header>

        <div class="addon-grid cpt-grid cpt-gap-4 cpt-grid-cols-1 sm:cpt-grid-cols-2 lg:cpt-grid-cols-3 xl:cpt-grid-cols-4">
          @for (addon of addOns; track addon.key) {
            <!-- Active when this addon has a featureFlag AND the embedder told us that flag is true.
                 Trial state is intentionally NOT gated here — if the partner has the addon flag set
                 (whether granted via trial or paid plan), show it as active so they know what they
                 currently have working. Permission/role add-ons (no featureFlag) never match — they
                 fall back to the default "Contact support" CTA until a parallel active-permissions
                 input lands. -->
            @let isActive = !!addon.featureFlag && _activeFeaturesSignal().has(addon.featureFlag);
            <article
              class="addon-card cpt-p-4 cpt-rounded"
              [class.addon-card--active]="isActive"
              [attr.aria-label]="isActive ? (('pricing_addons.' + addon.key + '_name') | translate) + ' — ' + ('pricing_addons.active_badge' | translate) : null"
            >
              <!-- HEAD: icon + title only. -->
              <div class="addon-card-head">
                <span class="addon-card-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" [innerHTML]="addon.iconSvg | calioSafe: 'html'"></svg>
                </span>
                <h3 class="addon-card-title cpt-text-base cpt-font-semibold cpt-font-sans cpt-m-0 cpt-text-calenso-black-color cpt-flex-1 cpt-min-w-0">
                  {{ ('pricing_addons.' + addon.key + '_name') | translate }}
                </h3>
              </div>
              <!-- BODY: description grows (flex:1); the price + CTA are pinned to the
                   bottom, so the price aligns across every card in the row regardless of
                   title/description length — and there's no gap under the title. -->
              <div class="addon-card-body cpt-mt-2">
                <p class="cpt-text-sm cpt-text-calenso-steel-blue-color cpt-mb-0 cpt-flex-1 cpt-leading-relaxed">
                  {{ ('pricing_addons.' + addon.key + '_description') | translate }}
                </p>
                <!-- Active partners see a badge instead of price/period — they already pay for this -->
                @if (isActive) {
                  <div class="addon-card-active-badge cpt-mt-3 cpt-inline-flex cpt-items-center cpt-gap-1">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span>{{ 'pricing_addons.active_badge' | translate }}</span>
                  </div>
                } @else {
                  <div class="addon-card-price cpt-mt-3">
                    <span class="addon-card-price-from">{{ 'pricing_addons.from_price' | translate }}</span>
                    {{ currencySymbolSignal() }}&nbsp;{{ addon.price[_currencySignal() | lowercase] | number: decimalDigits: decimalLocal }}
                    <span>/ {{ "pricing_table.year" | translate }}</span>
                  </div>
                }
                <!-- CTA differs by state:
                     - Active: "Active on your plan" label (no link — they have it already)
                     - Not active + dashboard/website mode: "Contact support" mailto CTA
                     - Sales mode: no CTA (sales reps don't need a mailto on every card) -->
                @if (isActive) {
                  <span
                    class="addon-card-cta addon-card-cta--active cpt-mt-2 cpt-text-sm cpt-font-semibold cpt-inline-flex cpt-items-center cpt-gap-1"
                    [attr.aria-label]="('pricing_addons.active_label' | translate) + ' — ' + (('pricing_addons.' + addon.key + '_name') | translate)"
                  >
                    {{ 'pricing_addons.active_label' | translate }}
                  </span>
                } @else if (_renderModeSignal() !== sales) {
                  <a
                    [href]="addOnContactHref()"
                    class="addon-card-cta cpt-mt-2 cpt-text-sm cpt-font-semibold cpt-text-calenso-neon-blue-color cpt-no-underline cpt-inline-flex cpt-items-center cpt-gap-1"
                    [attr.aria-label]="('pricing_addons.contact_cta' | translate) + ' — ' + (('pricing_addons.' + addon.key + '_name') | translate)"
                  >
                    {{ 'pricing_addons.contact_cta' | translate }} <span aria-hidden="true">→</span>
                  </a>
                }
              </div>
            </article>
          }
        </div>
      </section>

      @if (_renderModeSignal() === sales) {
        <footer class="pricing-shell-footer cpt-mt-10 cpt-text-center cpt-text-sm"
          [innerHTML]="'pricing_table.rights_reserved' | translate: { year: currentYear, brandName: brandParams.brandName, websiteUrl: brandParams.websiteUrl } | calioSafe: 'html'"
        >
        </footer>
      }
    </div>
  </section>
</main>

<ng-template #planUpgradeButton let-plan="plan">
  @if (_renderModeSignal() === dashboard) {
    <!-- Enterprise = mailto support, no self-serve flow ever. Always rendered
         regardless of current subscription / precedence — the partner can
         still write in even when they're on Team. -->
    @if (isEnterprisePlan(plan)) {
      <a class="cta-btn cta-btn--primary [.disabled-plan_&]:cpt-pointer-events-none"
         [href]="enterpriseContactHref()"
         [attr.aria-label]="'pricing_table.contact_support' | translate">
        {{ 'pricing_table.contact_support' | translate }}
      </a>
    } @else if (!_currentSubscriptionSignal() || isPartnerInTrialModeSignal()) {
      <!-- No active subscription OR trial: every non-Enterprise tier offers
           the self-serve Stripe upgrade. Basic precedence is 0, which used
           to collide with the default currentPlanPrecedenceSignal()=0 and
           silently render an empty button for Basic when there was no
           subscription at all. Gate on the subscription object (truthy)
           instead of the derived precedence. -->
      <ng-container [ngTemplateOutlet]="planButtonText" [ngTemplateOutletContext]="{ plan: plan , buttonText: 'upgrade_now' }" />
    } @else if (currentPlanPrecedenceSignal() === pricingPlanPrecedence[plan.id]) {
      <!-- Already on this tier — only show a CTA when flipping monthly→yearly. -->
      @if (_currentSubscriptionSignal()?.subscription_interval === monthly && _intervalSignal() === yearly) {
        <ng-container [ngTemplateOutlet]="planButtonText" [ngTemplateOutletContext]="{ plan: plan , buttonText: 'change_your_subscription' }" />
      } @else {
        <ng-container [ngTemplateOutlet]="emptyButtonText" />
      }
    } @else if (currentPlanPrecedenceSignal() > pricingPlanPrecedence[plan.id]) {
      <!-- Current plan is higher — downgrade not self-serve. -->
      <ng-container [ngTemplateOutlet]="emptyButtonText" />
    } @else {
      <!-- Upgrade path. -->
      <ng-container [ngTemplateOutlet]="planButtonText" [ngTemplateOutletContext]="{ plan: plan , buttonText: 'upgrade_now' }" />
    }
  }
</ng-template>

<ng-template #planButtonText let-buttonText="buttonText" let-plan="plan">
  <button
    type="button"
    class="cta-btn cta-btn--primary [.disabled-plan_&]:cpt-pointer-events-none"
    (click)="submit(plan,_intervalSignal(),_currencySignal())"
    [attr.aria-label]="'pricing_table.' + buttonText | translate"
  >
    {{ "pricing_table." + buttonText | translate }}
  </button>
</ng-template>

<ng-template #emptyButtonText>
  <div class="cpt-h-12"></div>
</ng-template>
